import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { Property } from 'csstype';
import React, { useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { toDataProps, useObserver, useObserverReturningStore } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { ObjectManager } from '../../../../concepts/objects';
import { Rect } from '../../../../platform/geom/Rect';
import { useResolve } from '../../../Container';
import { Camera } from '../../Camera';
import { useEditorSessionObserver } from '../../EditorSession';
import { PointerInfo } from '../../PointerInfo';
import { Physical } from '../../entities/Physical';
import { Physicals } from '../../entities/Physicals';
import { ToolInfo } from '../../tooling/ToolInfo';
import { useComponentObserver } from '../../visualization/ComponentObserver';
import { ActionLayout, Box, Float, remInPx } from './../../../../ui';
import {
    WidgetContainer,
    WidgetContainerAbsolutePositionConfig,
    WidgetContainerAttachedPositionConfig,
    WidgetContainerRelativePositionConfig,
    widgetSorter
} from './WidgetInterface';
import { WidgetLayout, WidgetLayoutDockingPoint } from './WidgetLayout';
import { WidgetManager } from './WidgetManager';
import { WidgetMount } from './WidgetMount';

const useWidgetsEnabled = () => {
    const pointer = useEditorSessionObserver(PointerInfo);
    const tool = useEditorSessionObserver(ToolInfo);
    return (
        !pointer.dragging &&
        tool.state !== 'transformPrimed' &&
        tool.state !== 'transforming'
    );
};

/* eslint-disable max-len */
export const WidgetLayer = React.memo(() => {

    const widgets = useObserver(useResolve(WidgetManager));
    const layout = useMemo(() => WidgetLayout.compute(widgets), [widgets]);
    const enabled = useWidgetsEnabled();

    return <Border id="widget-layer" enabled={enabled}>
        {/* {Object.keys(layout.attachments).map(target => <AttachmentPoint key={target} target={target} point={layout.attachments[target]} />)} */}
        {Object.entries(layout.relatives).map(([key, [wc, opts]]) =>
            <RelativePoint key={key} container={wc} {...opts} />)}
        {Object.entries(layout.absolutes).map(([key, [wc, opts]]) =>
            <AbsolutePoint key={key} container={wc} {...opts} />)}
        {Object.entries(layout.attachments).map(([key, [wc, opts]]) =>
            <AttachmentPoint key={key} container={wc} {...opts} />)}
        {Object.entries(layout.unmanageds).map(([key, [wc]]) =>
            <UnmanagedPoint key={key} container={wc} />)}
        <DockingPoint id="dpTopLeft" point={layout.topLeft} x="left" y="top" direction="column" orientation="row" align="flex-start" inlineAlign="flex-start" />
        <DockingPoint id="dpTop" point={layout.top} x="center" y="top" direction="column" orientation="row" align="center" inlineAlign="flex-start" />
        <DockingPoint id="dpTopRight" point={layout.topRight} x="right" y="top" direction="column" orientation="row-reverse" align="flex-end" inlineAlign="flex-start" />
        <DockingPoint id="dpRight" point={layout.right} x="right" y="middle" direction="row-reverse" orientation="column-reverse" align="center" inlineAlign="center" />
        <DockingPoint id="dpBottomRight" point={layout.bottomRight} x="right" y="bottom" direction="column-reverse" orientation="row-reverse" align="flex-end" inlineAlign="flex-end" />
        <DockingPoint id="dpBottom" point={layout.bottom} x="center" y="bottom" direction="column-reverse" orientation="row" align="center" inlineAlign="flex-end" />
        <DockingPoint id="dpBottomLeft" point={layout.bottomLeft} x="left" y="bottom" direction="column-reverse" orientation="row" align="flex-start" inlineAlign="flex-end" />
        <DockingPoint id="dpLeft" point={layout.left} x="left" y="middle" direction="row" orientation="column" align="center" inlineAlign="center" />
    </Border>;
});

const Border = styled.div<{ enabled: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    pointer-events: none;

    ${p => p.enabled ? '' : css`
        &, & * {
            pointer-events: none !important;
        }
    `}
`;

const ContainerBorder: LFC<{ container: WidgetContainer }> = ({ container, children }) =>
    (container.style === 'none')
        ? <>{children}</>
        : <Float shadow={container.style === 'floatHover' ? 'lg' : 'md'} inline>{children}</Float>;

const ContainerMount: LFC<{ container: WidgetContainer }> = ({ container }) => {
    const sorted = useMemo(
        () => Array.from(container.getState()).sort(widgetSorter),
        [container.getState()]
    );
    return <ContainerBorder container={container}>
        <Box id={`wc-${container.name}`} className={container.tag} {...toDataProps(container.data ?? {})}>
            <ActionLayout padding={scaleToPadding(container.scale)} mode={container.direction} lines={container.style === 'floatWithLines'}>
                {sorted.map((x: any) => <WidgetMount key={x.id} container={container} widget={x} />)}
            </ActionLayout>
        </Box>
    </ContainerBorder>;
};

interface DockingPointAnchorProps {
    x: 'left' | 'center' | 'right';
    y: 'top' | 'middle' | 'bottom';
    direction: Property.FlexDirection;
    orientation: Property.FlexDirection;
    align: Property.AlignItems;
    inlineAlign: Property.AlignItems;
}

const DockingPointAnchor = styled.div<DockingPointAnchorProps>`
    position: absolute;
    ${p => {
        switch (p.x) {
            case 'left': return 'left: 0;';
            case 'right': return 'right: 0;';
            default: return 'left: 50%; transform: translateX(-50%);';
        }
    }}
    ${p => {
        switch (p.y) {
            case 'top': return 'top: 0;';
            case 'bottom': return 'bottom: 0;';
            default: return 'top: 50%; transform: translateY(-50%);';
        }
    }}
    padding: 1rem;
    display: flex;
    flex-direction: ${p => p.direction};
    align-items: ${p => p.align};
    pointer-events: none;

    & > * {
        pointer-events: all;
        cursor: default;
    }

    & > div {
        display: flex;
        flex-direction: ${p => p.orientation};
        align-items: center;

        & > * {
            margin: 0.2rem;
        }
    }
`;

type DockingPointProps = { id: string, point: WidgetLayoutDockingPoint } & DockingPointAnchorProps;

const DockingPoint: LFC<DockingPointProps> = ({ id, point, ...styling }) => <>
    <DockingPointAnchor id={id} {...styling}>
        <div style={{ alignItems: styling.inlineAlign }}>
            {point.inline.map(x => <ContainerMount key={x.id} container={x} />)}
        </div>
        {point.stack.map(x => <div key={x.id} >
            <ContainerMount container={x} />
        </div>)}
    </DockingPointAnchor>
</>;

//
// Attachments
//

const AttachmentPointAnchor = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
`;

const AttachmentPointMount = styled.div`
    pointer-events: none;
    & > * {
        pointer-events: all;
        cursor: default;
    }
`;

type AttachmentPointProps = { container: WidgetContainer } & WidgetContainerAttachedPositionConfig;

const AttachmentPoint = React.memo<AttachmentPointProps>((props) => {

    const objects = useResolve(ObjectManager);
    const camera = useObserverReturningStore(useResolve(Camera));
    const zOrder = props.zOrder;
    const targets = useMemo(() => {
        if (props.to instanceof Rect) return [];
        const arr = Array.isArray(props.to) ? props.to : [props.to];
        return arr.map(id => objects.getMaybe(id, Physical)).filter(o => !!o);
    }, [props.to]);

    const ticker = useComponentObserver(targets);

    const bounds = useMemo(() => {
        const rect = Rect.fromPoints(camera.toView(props.to instanceof Rect ? props.to : Physicals.bounds(targets)));
        const view = camera.getViewFrame();
        if (view.contains(rect)) {
            return rect;
        }
        else {
            const clipped = Rect.fromEdges(
                Math.round(Math.max(rect.left, view.left)),
                Math.round(Math.max(rect.top, view.top)),
                Math.round(Math.min(rect.right, view.right)),
                Math.round(Math.min(rect.bottom, view.bottom)),
            );
            if (clipped.width < 100 || clipped.height < 100) {
                return Rect.empty;
            }
            else {
                return clipped;
            }
        }
    }, [ticker, camera.getState()]);

    const anchorStyle = useMemo(() => ({
        transform: `translate(${bounds.left}px, ${bounds.top}px)`,
        width: bounds.width,
        height: bounds.height,
    }), [bounds]);

    const { placement } = props;
    const [mainAxis, crossAxis] = (props.offset ?? [1, 0]).map(remInPx);

    const {
        x,
        y,
        reference,
        floating,
        strategy,
        refs,
        update,
    } = useFloating({
        placement,
        middleware: [offset({ mainAxis, crossAxis }), flip({ mainAxis: true, crossAxis: false }), shift({ padding: remInPx(6) })]
    });

    useEffect(() => {
        update();
    }, [camera.getState(), ticker, update]);

    useEffect(() => {
        if (refs.reference.current && refs.floating.current) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
    }, [refs.reference, refs.floating, update]);

    if (!bounds.width && !bounds.height) {
        return null;
    }

    return <>
        <AttachmentPointAnchor ref={reference} style={anchorStyle} />
        <AttachmentPointMount ref={floating}
            style={{
                position: strategy,
                top: y ?? '',
                left: x ?? '',
                zIndex: zOrder,
            }}
        >
            <ContainerMount container={props.container} />
        </AttachmentPointMount>
    </>;
});

//
// Relatives
//

const RelativePopperMount = styled.div`
    position: absolute;
`;

const RelativePopperInner = styled.div`
    pointer-events: all;
    cursor: default;
`;

const RelativePointAnchor: LFC<{ style: any }> = ({ style, children }) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    return <RelativePopperMount style={style}>
        <div ref={setRefElmt}></div>
        <RelativePopperInner ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            {children}
        </RelativePopperInner>
    </RelativePopperMount>;
};

type RelativePointProps = { container: WidgetContainer } & WidgetContainerRelativePositionConfig;

const RelativePoint = React.memo<RelativePointProps>((props) => {

    const objects = useResolve(ObjectManager);
    const camera = useObserverReturningStore(useResolve(Camera));
    const to = Array.isArray(props.to) ? props.to : [props.to];
    const targets = useMemo(() => to.map(id => objects.getMaybe(id, Physical)).filter(o => !!o), [to]);
    const ticker = useComponentObserver(targets);

    const styling = useMemo(() => {
        const vpt = camera.toView(props.at);
        return {
            transform: `translate(${Math.round(vpt.x)}px, ${Math.round(vpt.y)}px)`,
        };
    }, [props.at, camera.getState()]);

    return <RelativePopperMount style={styling}>
        <ContainerMount container={props.container} />
    </RelativePopperMount>;

});

//
// Absolutes
//

const AbsolutePopperMount = styled.div`
    position: absolute;
`;

const AbsolutePopperInner = styled.div`
    pointer-events: all;
    cursor: default;
`;

const AbsolutePointAnchor: LFC<{ style: any }> = ({ style, children }) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    return <AbsolutePopperMount style={style}>
        <div ref={setRefElmt}></div>
        <AbsolutePopperInner ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            {children}
        </AbsolutePopperInner>
    </AbsolutePopperMount>;
};

type AbsolutePointProps = { container: WidgetContainer } & WidgetContainerAbsolutePositionConfig;

const AbsolutePoint = React.memo<AbsolutePointProps>(({ container, at, relativeTo, zOrder }) => {

    const camera = useObserverReturningStore(useResolve(Camera));
    const styling = useMemo(() => {
        const vpt = relativeTo === 'model' ? camera.toView(at) : at;
        return {
            zIndex: zOrder,
            transform: `translate(${Math.round(vpt.x)}px, ${Math.round(vpt.y)}px)`,
        };
    }, [at, relativeTo, camera.getState()]);

    return <AbsolutePointAnchor style={styling}>
        <ContainerMount {...{ container }} />
    </AbsolutePointAnchor>;

});

const scaleToPadding = (s: number): 1 | 2 | 3 | 4 => {
    if (s < 1) s = 1;
    if (s > 4) s = 4;
    return s as any;
};


//
// Unmanaged
//

// const UnmanagedPopperMount = styled.div`
//     position: absolute;
// `;

// const UnmanagedPopperInner = styled.div`
//     pointer-events: all;
//     cursor: default;
// `;

// const UnmanagedPointAnchor: LFC<{ style: any }> = ({ style, children }) => {

//     const [refElmt, setRefElmt] = useState(null);
//     const [popElmt, setPopElmt] = useState(null);

//     const { styles, attributes } = usePopper(refElmt, popElmt, {
//         placement: 'bottom-start',
//     });

//     return <UnmanagedPopperMount style={style}>
//         <div ref={setRefElmt}></div>
//         <UnmanagedPopperInner ref={setPopElmt} style={styles.popper} {...attributes.popper}>
//             {children}
//         </UnmanagedPopperInner>
//     </UnmanagedPopperMount>;
// };

type UnmanagedPointProps = { container: WidgetContainer };

const UnmanagedPoint = React.memo<UnmanagedPointProps>(({ container }) => {

    return <>
        <ContainerMount {...{ container }} />
    </>;

});
